import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface ConstellationLoaderProps {
  message?: string;
}

export const ConstellationLoader: React.FC<ConstellationLoaderProps> = ({ 
  message = "Forming constellations..." 
}) => {
  const [visibleDots, setVisibleDots] = useState<number[]>([]);
  const [connections, setConnections] = useState<{ from: number; to: number }[]>([]);

  // Generate constellation points
  const constellationPoints = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30) * Math.PI / 180;
    const radius = 80 + Math.sin(i * 0.5) * 40;
    return {
      id: i,
      x: 200 + Math.cos(angle) * radius,
      y: 200 + Math.sin(angle) * radius,
    };
  });

  useEffect(() => {
    // Animate dots appearing
    const dotInterval = setInterval(() => {
      setVisibleDots(prev => {
        if (prev.length < constellationPoints.length) {
          return [...prev, prev.length];
        }
        clearInterval(dotInterval);
        return prev;
      });
    }, 200);

    // Add connections after dots appear
    const connectionTimeout = setTimeout(() => {
      const newConnections: { from: number; to: number }[] = [];
      constellationPoints.forEach((point, i) => {
        if (i > 0 && Math.random() > 0.4) {
          newConnections.push({ from: i - 1, to: i });
        }
        if (i < constellationPoints.length - 2 && Math.random() > 0.6) {
          newConnections.push({ from: i, to: i + 2 });
        }
      });
      setConnections(newConnections);
    }, 2000);

    return () => {
      clearInterval(dotInterval);
      clearTimeout(connectionTimeout);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-8">
      {/* Constellation SVG */}
      <div className="relative">
        <svg width="400" height="400" viewBox="0 0 400 400" className="opacity-80">
          {/* Connection lines */}
          {connections.map((connection, i) => {
            const fromPoint = constellationPoints[connection.from];
            const toPoint = constellationPoints[connection.to];
            return (
              <motion.line
                key={`line-${i}`}
                x1={fromPoint.x}
                y1={fromPoint.y}
                x2={toPoint.x}
                y2={toPoint.y}
                stroke="hsl(var(--primary))"
                strokeWidth="1"
                opacity="0.6"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.6 }}
                transition={{ duration: 1, delay: i * 0.1 }}
              />
            );
          })}
          
          {/* Constellation dots */}
          {visibleDots.map((dotIndex) => {
            const point = constellationPoints[dotIndex];
            return (
              <motion.circle
                key={`dot-${dotIndex}`}
                cx={point.x}
                cy={point.y}
                r="3"
                fill="hsl(var(--primary))"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: [0, 1.5, 1], 
                  opacity: [0, 1, 0.8] 
                }}
                transition={{ 
                  duration: 0.6,
                  times: [0, 0.6, 1]
                }}
              />
            );
          })}
          
          {/* Central glow */}
          <motion.circle
            cx="200"
            cy="200"
            r="60"
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="1"
            opacity="0.3"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.3 }}
            transition={{ duration: 1, delay: 1.5 }}
          />
        </svg>
        
        {/* Floating particles */}
        {Array.from({ length: 8 }).map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute w-1 h-1 bg-primary/40 rounded-full"
            style={{
              left: `${50 + Math.sin(i * 45 * Math.PI / 180) * 30}%`,
              top: `${50 + Math.cos(i * 45 * Math.PI / 180) * 30}%`,
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 0.8, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.25,
            }}
          />
        ))}
      </div>

      {/* Loading text with typing effect */}
      <div className="text-center space-y-2">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2 text-primary"
        >
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="text-lg"
          >
            ✨
          </motion.span>
          <span className="text-lg font-medium">{message}</span>
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-sm text-muted-foreground"
        >
          Analyzing context and mapping connections
        </motion.p>
      </div>
    </div>
  );
};